import { AwaitContainer, ScrollArea, Sentinel, Spinner, useInfiniteScroll, useReverseScroll } from '@ibsys/kit';
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateTerm, useDeleteTerm, useInfiniteTerms, useUpdateTerm } from '@/entities/term';
import { useHasPermission } from '@/entities/profile';
import { useShiftEnter } from '@/shared/hooks';
import { scrollIntoViewWithinContainer } from '@/shared/lib';
import { TermItem } from './TermItem';
import { NEW_ID, TermFormValues } from './types';
import { PageContainer } from '@/shared/ui';

export const TermsPage: FC = () => {
  const { t } = useTranslation(['terms', 'common']);
  const canEdit = useHasPermission('ADMIN_TERM');
  const bottomRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const shouldRestoreRef = useRef(false);
  const { captureScrollHeight, restoreScrollPosition } = useReverseScroll(viewportRef);
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteTerms({ limit: 10 });
  const createTerm = useCreateTerm();
  const updateTerm = useUpdateTerm();
  const deleteTerm = useDeleteTerm();
  const isRenderedFirstListRef = useRef(false);
  const [invalidTerms, setInvalidTerms] = useState<Record<number, boolean>>({});

  type TermItemValue = TermFormValues['terms'][number];

  const apiTerms: TermItemValue[] = useMemo(() => {
    const terms = data?.terms || [];

    if (terms.length === 0) {
      return [
        {
          id: NEW_ID,
          name: '',
          description: '',
          predicate: null,
        },
      ];
    }

    return terms.map((term) => ({
      id: term.id,
      name: term.name || '',
      description: term.description || '',
      predicate: term.predicate || null,
    }));
  }, [data?.terms]);

  const [terms, setTerms] = useState<TermItemValue[]>(apiTerms);

  useEffect(() => {
    setTerms(apiTerms);
  }, [apiTerms]);

  useEffect(() => {
    if (!isRenderedFirstListRef.current) {
      scrollIntoViewWithinContainer(bottomRef.current);
      if (!isLoading) {
        isRenderedFirstListRef.current = true;
      }
    }
  }, [isLoading]);

  useLayoutEffect(() => {
    if (shouldRestoreRef.current) {
      restoreScrollPosition();
      shouldRestoreRef.current = false;
    }
  }, [terms]);

  const handleLoadMore = useCallback(() => {
    captureScrollHeight();
    shouldRestoreRef.current = true;
    fetchNextPage();
  }, [captureScrollHeight, fetchNextPage]);

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    threshold: 100,
    direction: 'reverse',
    root: viewportRef,
  });

  const hasNewTerm = useMemo(() => {
    return terms.some((term) => term.id === NEW_ID);
  }, [terms]);

  const hasInvalidTerms = useMemo(() => Object.values(invalidTerms).some((value) => value), [invalidTerms]);

  // Handle save for a specific term
  const handleSave = useCallback(
    async (index: number, values: TermItemValue): Promise<TermItemValue> => {
      if (values.id === NEW_ID) {
        const response = await createTerm.mutateAsync({
          name: values.name,
          description: values.description,
          predicate:
            values.predicate?.dsl && values.predicate.dslHuman
              ? {
                  dsl: values.predicate.dsl!,
                  dslHuman: values.predicate.dslHuman!,
                }
              : null,
        });

        const saved: TermItemValue = {
          id: response.id,
          name: response.name || values.name,
          description: response.description || values.description,
          predicate: response.predicate || null,
        };

        setTerms((prev) => prev.map((term, i) => (i === index ? saved : term)));

        return saved;
      }

      const updated = await updateTerm.mutateAsync({
        id: values.id!,
        payload: {
          name: values.name,
          description: values.description,
          predicate:
            values.predicate?.dsl && values.predicate.dslHuman
              ? {
                  dsl: values.predicate.dsl!,
                  dslHuman: values.predicate.dslHuman!,
                }
              : null,
        },
      });

      const saved: TermItemValue = {
        id: updated.id,
        name: updated.name || values.name,
        description: updated.description || values.description,
        predicate: updated.predicate || null,
      };

      setTerms((prev) => prev.map((term, i) => (i === index ? saved : term)));

      return saved;
    },
    [createTerm, updateTerm],
  );

  // Handle add new term
  const handleAdd = useCallback(() => {
    if (hasNewTerm) {
      return; // Can't add if there's already a new term
    }

    setTerms((prev) => [
      ...prev,
      {
        id: NEW_ID,
        name: '',
        description: '',
        predicate: null,
      },
    ]);
  }, [hasNewTerm]);

  // Handle Shift+Enter for add
  const handleShiftEnter = useCallback(async () => {
    handleAdd();
    setTimeout(() => {
      scrollIntoViewWithinContainer(bottomRef.current);
    });
  }, [handleAdd]);

  // Enable Shift+Enter hook — только при праве на редактирование
  useShiftEnter(handleShiftEnter, canEdit && !hasNewTerm && !hasInvalidTerms);

  // Handle remove term
  const handleRemove = useCallback(
    async (index: number) => {
      const term = terms[index];

      if (!term) {
        return;
      }

      if (term.id === NEW_ID) {
        setTerms((prev) => prev.filter((_, i) => i !== index));
      } else {
        await deleteTerm.mutateAsync(term.id!);
        setTerms((prev) => prev.filter((_, i) => i !== index));
      }
      setInvalidTerms((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    },
    [terms, deleteTerm],
  );

  return (
    <PageContainer enableScroll={false} outerClassName={'p-0 md:p-0'} limitedWidth={false}>
      <ScrollArea
        className="flex-1 h-full"
        viewPortProps={{ ref: viewportRef, className: 'flex flex-col h-full p-2 md:p-8' }}
      >
        <AwaitContainer isLoading={isLoading} className="overflow-hidden flex flex-col h-full">
          <div className="w-full h-full max-w-7xl mx-auto">
            <Sentinel ref={sentinelRef}>
              {isFetchingNextPage && (
                <div className="w-full flex justify-center py-6">
                  <Spinner className="size-8" />
                </div>
              )}
            </Sentinel>
            <div className="flex flex-col gap-4 p-1 pb-3">
              {terms.length === 0 && !isLoading && (
                <div className="text-center text-muted-foreground py-8">{t('noTerms')}</div>
              )}
              {terms.map((term, index, array) => (
                <TermItem
                  key={`${term.id ?? index}-${array.length}`}
                  term={term}
                  onRemove={() => handleRemove(index)}
                  onAdd={handleAdd}
                  onSave={(values) => handleSave(index, values)}
                  isLast={index === array.length - 1}
                  canEdit={canEdit}
                  canAddNew={!hasNewTerm}
                  onValidate={(hasError) =>
                    setInvalidTerms((prev) => {
                      if (prev[index] === hasError) {
                        return prev;
                      }
                      return { ...prev, [index]: hasError };
                    })
                  }
                />
              ))}
            </div>
            <Sentinel ref={bottomRef} />
          </div>
        </AwaitContainer>
      </ScrollArea>
    </PageContainer>
  );
};
